import axiosInstance from 'shared/services/api/axiosInstance';

/** Periods the server names itself (ending now); any other is sent as from/to. */
const SERVER_WINDOWS = ['24h', '7d', '14d', '30d', '3m'];

/**
 * The period as the api takes it. `period` is the time filter's
 * { preset, from, to }, or a bare window name.
 */
export function periodParams(period) {
  if (typeof period === 'string') return { window: period };
  if (period?.preset && SERVER_WINDOWS.includes(period.preset)) return { window: period.preset };
  const out = {};
  if (period?.from) out.from = period.from;
  if (period?.to) out.to = period.to;
  return Object.keys(out).length ? out : { window: '7d' };
}

/** The filter bar's selections; null = everything, so nothing is sent. */
export function filterParams({ agents = null, tools = null, categories = null, search = '' } = {}) {
  const out = {};
  if (Array.isArray(agents)) out.agents = JSON.stringify(agents);
  if (Array.isArray(tools)) out.tools = JSON.stringify(tools);
  if (Array.isArray(categories)) out.categories = JSON.stringify(categories);
  if (search && search.trim()) out.search = search.trim();
  return out;
}

/**
 * One page of failures grouped by agent · tool · error type · class.
 * @returns {Promise<{window, from, to, clamped, days, total, clustersTotal, hasMore, truncated, clusters}>}
 */
export async function listFailures(teamId, {
  period = '7d', filters = {}, sort = 'last_seen', dir = 'desc', offset = 0, limit = 50,
} = {}) {
  const params = { team_id: teamId, ...periodParams(period), ...filterParams(filters), sort, dir, offset, limit };
  const { data } = await axiosInstance.get('/failures', { params });
  const clusters = data?.clusters || [];
  return {
    window: data?.window || null,
    from: data?.from || null,
    to: data?.to || null,
    clamped: Boolean(data?.clamped),
    days: data?.days || [],
    total: data?.total || 0,
    clustersTotal: data?.clusters_total ?? clusters.length,
    hasMore: Boolean(data?.has_more),
    truncated: Boolean(data?.truncated),
    clusters,
  };
}

/**
 * The filter popup's choices for a period: agents, tools and categories.
 * `q` narrows them and `limit` caps each list (the totals say how many matched).
 */
export async function getFailureFacets(teamId, { period = '7d', q = '', limit = null } = {}) {
  const params = { team_id: teamId, ...periodParams(period) };
  if (q) params.q = q;
  if (limit) params.limit = limit;
  const { data } = await axiosInstance.get('/failures/facets', { params });
  const agents = data?.agents || [];
  const tools = data?.tools || [];
  return {
    agents,
    tools,
    categories: data?.categories || [],
    agents_total: data?.agents_total ?? agents.length,
    tools_total: data?.tools_total ?? tools.length,
  };
}

/**
 * The dashboard above the table, under the table's period and filters.
 * @returns {Promise<{window, bucket, clamped, total, truncated, series, by_category, by_tool, by_agent, by_error_type, split, rate}>}
 */
export async function getFailureSummary(teamId, { period = '7d', filters = {} } = {}) {
  const params = { team_id: teamId, ...periodParams(period), ...filterParams(filters) };
  const { data } = await axiosInstance.get('/failures/summary', { params });
  return {
    window: data?.window || null,
    bucket: data?.bucket || 'day',
    clamped: Boolean(data?.clamped),
    total: data?.total || 0,
    truncated: Boolean(data?.truncated),
    series: data?.series || [],
    by_category: data?.by_category || [],
    by_tool: data?.by_tool || [],
    by_agent: data?.by_agent || [],
    by_error_type: data?.by_error_type || [],
    // The over-time chart's Tool and Agent tabs read these; without them
    // every tab drew the category split.
    split: data?.split || null,
    rate: data?.rate || null,
  };
}

/** "in the last 7d", "today", "from … to …": the period in words, for Ask. */
export function periodText(period) {
  if (!period || typeof period === 'string') return `in the last ${period || '7d'}`;
  if (period.preset === 'today' || period.preset === 'yesterday') return period.preset;
  if (period.preset && period.preset !== 'custom') return `in the last ${period.preset}`;
  return `from ${period.from} to ${period.to}`;
}

/**
 * Where a row opens: the failing spans themselves, or the failed runs when no
 * span took the blame. Same period as the tab, so the counts agree.
 */
export function failureLink(team, cluster, period) {
  const qs = new URLSearchParams({ status: 'error' });
  if (typeof period === 'string') qs.set('preset', period);
  else if (period?.preset && period.preset !== 'custom') qs.set('preset', period.preset);
  else {
    qs.set('preset', 'custom');
    if (period?.from) qs.set('time_start', period.from);
    if (period?.to) qs.set('time_end', period.to);
  }
  if (cluster.agent_name) qs.set('agent_name', cluster.agent_name);
  if (cluster.kind === 'run') return `/${encodeURIComponent(team)}/agent-analytics/runs?${qs}`;
  if (cluster.tool) qs.set('tool_name', cluster.tool);
  return `/${encodeURIComponent(team)}/agent-analytics/traces?${qs}`;
}

/**
 * The individual failures behind one row, newest first; `before` pages. The
 * table's period and search, so the drawer lists what the row counted.
 */
export async function listFailureOccurrences(teamId, cluster, { period = '7d', search = '', before = null, limit = 50 } = {}) {
  const params = {
    team_id: teamId,
    ...periodParams(period),
    ...filterParams({ search }),
    kind: cluster.kind === 'run' ? 'run' : 'tool',
    error_class: cluster.error_class,
    limit,
  };
  if (cluster.agent_name) params.agent = cluster.agent_name;
  if (cluster.tool) params.tool = cluster.tool;
  if (cluster.error_type) params.error_type = cluster.error_type;
  if (before) params.before = before;
  const { data } = await axiosInstance.get('/failures/occurrences', { params });
  return { items: data?.items || [], nextBefore: data?.next_before || null };
}

/** The alert editor, filled in to watch exactly this failure. */
export function failureAlertLink(team, cluster) {
  const qs = new URLSearchParams({ template: cluster.kind === 'run' ? 'run_failures' : 'tool_failures' });
  if (cluster.agent_name) qs.set('agent', cluster.agent_name);
  if (cluster.kind !== 'run' && cluster.tool) qs.set('tool', cluster.tool);
  if (cluster.error_type) qs.set('error_type', cluster.error_type);
  return `/${encodeURIComponent(team)}/alerts/new?${qs}`;
}
